import { Text } from '../../primitives/Text';
import { View } from '../../primitives/View';
import { cn } from '../../utils/cn';
import { KeybindChip } from '../KeybindChip/KeybindChip';
import { ICON_SIZE_FOR } from './SegmentedControl.icons';
import type {
  Segment,
  SegmentedControlActiveStyle,
  SegmentedControlSize,
} from './SegmentedControl.types';
import {
  KEYBIND_CHIP_SIZE,
  segmentLabelColorVariants,
  segmentTextVariants,
  segmentVariants,
} from './SegmentedControl.variants';

/** Derived render flags for one segment. */
export interface SegmentState {
  isActive: boolean;
  disabled: boolean;
  /** Icon present with no label (and no web keycap) → square icon-only cell. */
  iconOnly: boolean;
  /** Icon+label or a rendered keycap → asymmetric padding (pr = pl + 4). */
  iconText: boolean;
  /** Keycap renders in this segment (web only). */
  keybind: boolean;
}

export function segmentStateOf<V extends string>(
  item: Segment<V>,
  value: V,
  controlDisabled: boolean,
  // Whether keybind keycaps render on this platform (true on web only). A
  // keycap gives an otherwise icon-only segment visible width, so it renders
  // as a regular (non-square) cell there; native never shows keycaps and the
  // same segment stays a square icon-only cell.
  withKeybind = false,
): SegmentState {
  const hasIcon = item.icon !== undefined;
  const hasLabel = item.label != null;
  const keybind = withKeybind && item.keybinding != null;
  return {
    isActive: item.value === value,
    disabled: controlDisabled || !!item.disabled,
    iconOnly: hasIcon && !hasLabel && !keybind,
    iconText: (hasIcon && hasLabel) || keybind,
    keybind,
  };
}

/** Full className for one segment cell. */
export function segmentClassName(
  state: SegmentState,
  size: SegmentedControlSize,
  activeStyle: SegmentedControlActiveStyle,
  fullWidth: boolean,
  extra?: string,
): string {
  return cn(
    segmentVariants({
      size,
      active: state.isActive,
      activeStyle,
      iconOnly: state.iconOnly,
      iconText: state.iconText,
      keybind: state.keybind,
      fullWidth,
      disabled: state.disabled,
    }),
    extra,
  );
}

function isTextLike(c: unknown): boolean {
  if (c == null || typeof c === 'boolean') return false;
  if (typeof c === 'string' || typeof c === 'number') return true;
  if (Array.isArray(c)) return c.every(isTextLike);
  return false;
}

/**
 * The in-flow content of a segment — leading icon (or icon-only), label, and
 * the optional keybind keycap. Wrapped in a `relative z-10` row so it paints
 * ABOVE the absolutely-positioned active pill on web (the pill is `z-0`).
 *
 * `iconColor` is `currentColor` on web (inherits the segment's text colour) and
 * a resolved hex on native. `withKeybind` is web-only — the keycap never
 * renders on native (no keyboard; Chivo Mono face isn't bundled there).
 * `active` picks the keybind chip tone: Primary on the active segment,
 * Neutral on inactive ones (Figma 4036:23072 / 4036:27661).
 */
export function SegmentContent<V extends string>({
  item,
  size,
  active,
  activeStyle,
  iconColor,
  withKeybind,
}: {
  item: Segment<V>;
  size: SegmentedControlSize;
  active: boolean;
  activeStyle: SegmentedControlActiveStyle;
  iconColor: string;
  withKeybind: boolean;
}) {
  const Icon = item.icon;
  // Colour goes on the <Text> itself — RN Text doesn't inherit `text-*` from
  // the parent Pressable (web would cascade, but the explicit class is
  // identical there).
  const textClass = cn(
    segmentTextVariants({ size }),
    segmentLabelColorVariants({ active, activeStyle }),
  );
  const chipSize = KEYBIND_CHIP_SIZE[size];
  // Chip tone (Figma 4036:23072 / 4036:27661): Primary on the active segment,
  // Neutral (= the shared chip's `secondary` colorway) on inactive ones.
  const tone = active ? ('primary' as const) : ('secondary' as const);
  const showKeybind = withKeybind && item.keybinding != null;
  // Keybind spacing (4036:23075): label→keycap = 8px on `xxs`, 12px elsewhere
  // (the row's own 6px gap + this `ml`). No trailing margin — the segment's
  // own asymmetric padding (`iconText`/`keybind` compounds, pr = pl + 4)
  // already provides the keycap's trailing inset. Web-only.
  const keybindSpacing = size === 'xxs' ? 'ml-0.5' : 'ml-1.5';

  // `numberOfLines={1}` is the NATIVE half of `segmentTextVariants`'
  // `whitespace-nowrap`, which is web-only: a segment label is a name, and a
  // name that wraps turns a 40px-tall cell into a two-line box that pushes the
  // whole track taller. Native had no equivalent, so a label too long for its
  // cell wrapped instead of ellipsising. Web already clips; this makes native
  // agree.
  const label =
    item.label != null && isTextLike(item.label) ? (
      <Text className={textClass} numberOfLines={1}>
        {item.label}
      </Text>
    ) : (
      item.label
    );

  return (
    <View className="relative z-10 inline-flex flex-row items-center justify-center gap-1.5">
      {Icon ? <Icon color={iconColor} size={ICON_SIZE_FOR[size]} /> : null}
      {label}
      {showKeybind ? (
        <KeybindChip variant={tone} chipSize={chipSize} className={keybindSpacing}>
          {item.keybinding!}
        </KeybindChip>
      ) : null}
    </View>
  );
}
